import React from "react"
import withUseRouter, { UseRouterParams } from "services/router"
import {
	MatesComponent,
	MatesComponentState,
} from "components/mates-component"
import MatchProposalSearchbar from "components/match-proposal/searchbar/match-proposal-searchbar"
import {
	ErrorResponse,
	Loading,
	RestResponse,
} from "@sinossi/mates-react-library"
import {
	PageHeader,
	RoutesData,
} from "components/commons"
import {
	Box,
	Pagination,
} from "@mui/material"
import {
	AssetMatchBrand,
	AssetMatchBrandWithImage,
	PaginatedResponse,
} from "models"
import { MatchService } from "services/match.service"
import { MatchProposalFilter } from "components/match-proposal/model"
import MatchProposalItem from "components/match-proposal/match-proposal-list/match-proposal-item"


interface MatchProposalProps extends UseRouterParams {

}

interface MatchProposalState extends MatesComponentState {
	loading: boolean
	matchList: PaginatedResponse<AssetMatchBrandWithImage> | null
	filter: MatchProposalFilter
}

class MatchProposal extends React.Component<MatchProposalProps, MatchProposalState> {
	private readonly matesComponent: MatesComponent<MatchProposalProps, MatchProposalState> = new MatesComponent(this)

	constructor(props: MatchProposalProps)
	{
		super(props)
		this.state = {
			...this.matesComponent.setupState(),
			loading: true,
			matchList: null,
			filter: new MatchProposalFilter()
		}
	}

	componentDidMount() {
		this.filteredMatchList()
	}

	private filteredMatchList = () => {
		MatchService.filteredMatchesList(this.state.filter, this.filteredMatchListCallback, this.matesComponent.onError)
	}

	private filteredMatchListCallback = (response: RestResponse<PaginatedResponse<AssetMatchBrandWithImage>, ErrorResponse>) => {
		if(response.hasError()){
			this.matesComponent.onError(response)
			this.setState(() => ({
				loading: false
			}))
		}

		this.setState(() => ({
			loading: false,
			matchList: response.data
		}))
	}

	private getMatches = (request: MatchProposalFilter) => {
		Object.keys(request).forEach((key) => {
			if((request as any)[key] === ""){
				(request as any)[key] = null
			}
		})

		const filter = Object.assign(request)

		this.setState(() => ({filter, loading: true}), this.filteredMatchList)
	}

	private handlePagination = (_: any, value: number) => {
		if (value) {
			this.setState((prevState) => ({
				filter: {
					...prevState.filter,
					pageId: value - 1
				},
				loading: true,
			}), this.filteredMatchList)
		}
	}

	private deduplicateMatchesByAsset = (matches: AssetMatchBrandWithImage[]): AssetMatchBrandWithImage[] => {
		if (!matches || matches.length === 0) {
			return []
		}

		const assetMap = new Map<string, { best: AssetMatchBrandWithImage; others: AssetMatchBrand[] }>()

		matches.forEach((match) => {
			const assetId = match.match.asset.id
			const additionalProposals = match.allProposals ?? []
			const normalizedMatch: AssetMatchBrandWithImage = {
				...match,
				allProposals: [],
			}

			if (!assetMap.has(assetId)) {
				assetMap.set(assetId, {
					best: normalizedMatch,
					others: [...additionalProposals],
				})
				return
			}

			const entry = assetMap.get(assetId)!
			entry.others.push(match.match, ...additionalProposals)

			const currentCompatibility = match.match.compatibility ?? 0
			const bestCompatibility = entry.best.match.compatibility ?? 0

			if (currentCompatibility > bestCompatibility) {
				entry.others.push(entry.best.match)
				entry.best = normalizedMatch
			}
		})

		return Array.from(assetMap.values()).map(({ best, others }) => {
			const uniqueProposals = new Map<string, AssetMatchBrand>()
			others.forEach((proposal) => {
				if (proposal && proposal.id) {
					uniqueProposals.set(proposal.id, proposal)
				}
			})

			uniqueProposals.delete(best.match.id)

			const sortedProposals = Array.from(uniqueProposals.values()).sort(
				(a, b) => (b.compatibility ?? 0) - (a.compatibility ?? 0)
			)

			return {
				...best,
				allProposals: sortedProposals,
			}
		})
	}

	render() {
		return (
			<Box sx={{ width: '100%', pt: 2, position: "relative" }}>
				<Loading show={this.state.loading} />
				<PageHeader
					title={RoutesData.MATCH_PROPOSAL.title}
					paths={[RoutesData.DASHBOARD, RoutesData.MATCH_PROPOSAL]}
				/>
				<MatchProposalSearchbar
					handleCallback={this.getMatches}
				/>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						mt: 4,
					}}
				>
					<Pagination
						showFirstButton
						showLastButton
						page={this.state.matchList ? this.state.matchList.pagination.currentPage + 1 : 1}
						count={this.state.matchList ? this.state.matchList.pagination.totalPages : 1}
						onChange={this.handlePagination}
						siblingCount={2}
						sx={{
							mb: 4,
							display: "flex",
							width: "100%",
							justifyContent: "right",
						}}
					/>
					{
						this.state.matchList && this.deduplicateMatchesByAsset(this.state.matchList.items).map((match, index) => (
							<MatchProposalItem
								key={`${match.match.asset.id}-${match.match.id}-${index}`}
								match={match}
							/>
						))
					}
				</Box>
			</Box>
		)
	}
}

export default withUseRouter(MatchProposal)
